import { useRef, useState } from "react";
import { format } from "date-fns";
import { toBase64 } from "./helper";
import { useRefreshGuard } from "./refreshGuard";

// Editors that can hold files until their Save (keys match the views' fileType argument)
export const DESCRIPTION_FILES = "NEW_FILE_DESC";
export const COMMENT_FILES = "NEW_FILE_COMMENT";

let fileIdCounter = 0;
const createFileId = () => `file-${Date.now()}-${fileIdCounter++}`;

// iOS names every photo taken from the file picker "image.jpg"
const GENERIC_PHOTO_NAME = /^image\.(jpe?g|png|heic|heif)$/i;

const splitName = (name) => {
  const dotIndex = name.lastIndexOf(".");
  return dotIndex > 0 ? [name.slice(0, dotIndex), name.slice(dotIndex)] : [name, ""];
};

// Gives generic photo names a timestamp and adds " (2)", " (3)"... to names already taken,
// so attachments can be told apart in the lists
const makeUniqueName = (name, takenNames) => {
  let uniqueName = name;
  if (GENERIC_PHOTO_NAME.test(name)) {
    const [, extension] = splitName(name);
    uniqueName = `image_${format(new Date(), "yyyyMMdd_HHmmss")}${extension}`;
  }
  if (!takenNames.includes(uniqueName)) return uniqueName;

  const [stem, extension] = splitName(uniqueName);
  let counter = 2;
  while (takenNames.includes(`${stem} (${counter})${extension}`)) counter++;
  return `${stem} (${counter})${extension}`;
};

// Name to show for an attachment. Names that already carry an extension are shown as is
// (appending the suffix again showed "photo.jpg.jpeg")
export const attachmentDisplayName = (file, suffix) => {
  const [, extension] = splitName(file?.EXTFILEDES || "");
  return extension || !suffix ? file?.EXTFILEDES : `${file.EXTFILEDES}.${suffix}`;
};

/**
 * Attachment handling shared by tasks and support calls:
 * - pendingFiles: files attached inside an editor, kept per editor until its Save.
 *   Cancel clears them; files that fail to upload stay pending so Save can retry them.
 * - directUploads: files added from the Attachments tab, uploaded right away (no comment needed),
 *   shown with their progress / error until they finish.
 * Uploaded files are handed to onUploaded - they're added to the attachments list only then.
 *
 * uploadFile(file) must return { error } ("" / undefined on success) and may return { attachment }.
 */
export const useAttachmentUploads = ({ uploadFile, onUploaded, existingFiles = [] }) => {
  const [pendingFiles, setPendingFiles] = useState({
    [DESCRIPTION_FILES]: [],
    [COMMENT_FILES]: [],
  });
  const [directUploads, setDirectUploads] = useState([]);

  // Read inside async flows, where the state captured at call time may be stale
  const pendingFilesRef = useRef(pendingFiles);
  pendingFilesRef.current = pendingFiles;
  const directUploadsRef = useRef(directUploads);
  directUploadsRef.current = directUploads;

  // Files waiting for an editor's Save, or still uploading, would be lost by a pull-to-refresh
  useRefreshGuard(
    () =>
      Object.values(pendingFilesRef.current).some((files) => files.length > 0) ||
      directUploadsRef.current.some((upload) => upload.status === "uploading")
  );

  const getTakenNames = () => [
    ...existingFiles.map((file) => file.EXTFILEDES),
    ...Object.values(pendingFilesRef.current)
      .flat()
      .map((file) => file.EXTFILEDES),
    ...directUploadsRef.current.map((file) => file.EXTFILEDES),
  ];

  const readFiles = async (fileList) => {
    const takenNames = getTakenNames();
    const files = [];
    for (const file of Array.from(fileList || [])) {
      const name = makeUniqueName(file.name, takenNames);
      takenNames.push(name);
      files.push({ id: createFileId(), EXTFILEDES: name, EXTFILENAME: await toBase64(file) });
    }
    return files;
  };

  const addPendingFiles = async (editorKey, fileList) => {
    const files = await readFiles(fileList);
    setPendingFiles((prev) => ({ ...prev, [editorKey]: [...(prev[editorKey] || []), ...files] }));
  };

  const removePendingFile = (editorKey, fileToRemove) => {
    setPendingFiles((prev) => ({
      ...prev,
      [editorKey]: (prev[editorKey] || []).filter((file) => file.id !== fileToRemove.id),
    }));
  };

  const clearPendingFiles = (editorKey) => {
    setPendingFiles((prev) => ({ ...prev, [editorKey]: [] }));
  };

  const sendFile = async (file) => {
    const fileData = { EXTFILEDES: file.EXTFILEDES, EXTFILENAME: file.EXTFILENAME };
    const { error, attachment } = await uploadFile(fileData);
    if (!error) {
      onUploaded({ ...fileData, EXTFILENUM: attachment?.EXTFILENUM });
    }
    return error;
  };

  // Uploads an editor's pending files; returns the last error ("" when all succeeded)
  const uploadPendingFiles = async (editorKey) => {
    let lastError = "";
    for (const file of pendingFilesRef.current[editorKey] || []) {
      const error = await sendFile(file);
      if (error) {
        lastError = error;
      } else {
        removePendingFile(editorKey, file);
      }
    }
    return lastError;
  };

  const runDirectUpload = async (file) => {
    let error;
    try {
      error = await sendFile(file);
    } catch (err) {
      // Runs in the background - a throw must still leave the row in a retryable state
      console.error("Attachment upload threw", file.EXTFILEDES, err);
      error = "error:error_failed_upload_file";
    }
    if (error) {
      setDirectUploads((prev) =>
        prev.map((upload) => (upload.id === file.id ? { ...upload, status: "failed", error } : upload))
      );
    } else {
      setDirectUploads((prev) => prev.filter((upload) => upload.id !== file.id));
    }
  };

  // Attachments tab: resolves once the files are read, the uploads continue in the background
  const uploadDirectly = async (fileList) => {
    const files = await readFiles(fileList);
    setDirectUploads((prev) => [...prev, ...files.map((file) => ({ ...file, status: "uploading" }))]);

    const uploadOneByOne = async () => {
      for (const file of files) {
        await runDirectUpload(file);
      }
    };
    uploadOneByOne();
  };

  const retryDirectUpload = (id) => {
    const file = directUploadsRef.current.find((upload) => upload.id === id);
    if (!file) return;
    setDirectUploads((prev) =>
      prev.map((upload) => (upload.id === id ? { ...upload, status: "uploading", error: "" } : upload))
    );
    runDirectUpload(file);
  };

  const dismissDirectUpload = (id) => {
    setDirectUploads((prev) => prev.filter((upload) => upload.id !== id));
  };

  return {
    pendingFiles,
    addPendingFiles,
    removePendingFile,
    clearPendingFiles,
    uploadPendingFiles,
    directUploads,
    uploadDirectly,
    retryDirectUpload,
    dismissDirectUpload,
  };
};
